/**
 * Email Settings Tab - Phase 9.1a Email Integration + R2 Email Subscriptions
 *
 * Configure email sending for proposals and invoices:
 * - Reply-To address (where client replies go)
 * - BCC list (team members who get copies)
 * - Enable/disable toggle
 * - Email signature (name, title, company, phone, email)
 *
 * R2: Email Notifications section with per-channel toggles and email address.
 */
import { useState, useEffect } from 'react';
import { useEmailSettings, useUpdateEmailSettings, useEmailNotificationPreferences, useUpdateEmailNotificationPreferences } from '@/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Mail, Save, AlertCircle, CheckCircle2, User, Bell } from 'lucide-react';
import type { EmailSettings, EmailSignature } from '@/lib/schemas';

// ============================================================================
// Email Notifications section constants (R2)
// ============================================================================

const EMAIL_NOTIFICATION_TOGGLES = [
  {
    key: 'permit_reminders',
    label: 'Permit expiry reminders',
    description: 'Get emailed when permits are approaching their expiry date (60, 30, 7 days before)',
    defaultOn: true,
  },
  {
    key: 'daily_digest',
    label: 'Daily operational digest',
    description: 'Morning summary of what needs attention — overdue invoices, pending proposals, ball-in-court items',
    defaultOn: true,
  },
  {
    key: 'collection_reminders',
    label: 'Collection follow-up reminders',
    description: 'Get emailed when a scheduled payment follow-up is due',
    defaultOn: false,
  },
  {
    key: 'invoice_sent_confirmation',
    label: 'Invoice sent confirmation',
    description: 'Get a confirmation when an invoice is successfully emailed to a client',
    defaultOn: true,
  },
] as const;

export function EmailSettingsTab() {
  const { user } = useAuth();

  // Fetch current settings
  const { data: settings, isLoading } = useEmailSettings();
  const updateEmailSettings = useUpdateEmailSettings();

  // Email notification preferences (R2)
  const { data: notifPrefs, isLoading: notifLoading } = useEmailNotificationPreferences();
  const updateNotifPrefs = useUpdateEmailNotificationPreferences();

  // Form state
  const [replyTo, setReplyTo] = useState('');
  const [bccList, setBccList] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [signature, setSignature] = useState<EmailSignature>({
    name: '',
    title: '',
    company: '',
    phone: '',
    email: '',
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Email notification preferences form state (R2)
  const [emailTypes, setEmailTypes] = useState<Record<string, boolean>>({});
  const [emailAddress, setEmailAddress] = useState('');
  const [notifHasChanges, setNotifHasChanges] = useState(false);

  // Initialize form with fetched data
  useEffect(() => {
    if (settings) {
      setReplyTo(settings.reply_to || '');
      setBccList(settings.bcc_list?.join(', ') || '');
      setEnabled(settings.enabled ?? false);
      setSignature({
        name: settings.signature?.name || '',
        title: settings.signature?.title || '',
        company: settings.signature?.company || '',
        phone: settings.signature?.phone || '',
        email: settings.signature?.email || '',
      });
      setHasChanges(false);
    }
  }, [settings]);

  // Initialize email notification preferences form state (R2)
  useEffect(() => {
    if (notifPrefs) {
      // Merge stored email_types with defaults so all 4 keys are always present
      const merged: Record<string, boolean> = {};
      for (const toggle of EMAIL_NOTIFICATION_TOGGLES) {
        merged[toggle.key] = notifPrefs.email_types?.[toggle.key] ?? toggle.defaultOn;
      }
      setEmailTypes(merged);
      // email_address: use stored value, fall back to user's login email
      setEmailAddress(notifPrefs.email_address ?? user?.email ?? '');
      setNotifHasChanges(false);
    }
  }, [notifPrefs, user?.email]);

  const handleReplyToChange = (value: string) => {
    setReplyTo(value);
    setHasChanges(true);
  };

  const handleBccListChange = (value: string) => {
    setBccList(value);
    setHasChanges(true);
  };

  const handleEnabledChange = (value: boolean) => {
    setEnabled(value);
    setHasChanges(true);
  };

  const handleSignatureChange = (field: keyof EmailSignature, value: string) => {
    setSignature((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Only include signature if at least one field is filled
    const hasSignature = Object.values(signature).some((v) => v && v.trim());
    const signatureData = hasSignature
      ? {
          name: signature.name || null,
          title: signature.title || null,
          company: signature.company || null,
          phone: signature.phone || null,
          email: signature.email || null,
        }
      : null;

    const data: Partial<EmailSettings> = {
      reply_to: replyTo || null,
      bcc_list: bccList
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean),
      enabled,
      signature: signatureData,
    };

    await updateEmailSettings.mutateAsync(data);
    setHasChanges(false);
  };

  const handleNotifToggle = (key: string) => {
    setEmailTypes((prev) => ({ ...prev, [key]: !prev[key] }));
    setNotifHasChanges(true);
  };

  const handleNotifEmailChange = (value: string) => {
    setEmailAddress(value);
    setNotifHasChanges(true);
  };

  const handleNotifSave = async () => {
    await updateNotifPrefs.mutateAsync({
      email_types: emailTypes,
      email_address: emailAddress.trim() || null,
    });
    setNotifHasChanges(false);
  };

  // Validation: Can't enable without reply_to
  const canEnable = Boolean(replyTo);
  const showEnableWarning = enabled && !canEnable;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <Mail className="w-5 h-5 text-slate-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Email Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure how proposals and invoices are sent via email
          </p>
        </div>
      </div>

      {/* Status indicator */}
      <div
        className={`flex items-center gap-3 p-4 rounded-lg ${
          enabled && canEnable
            ? 'bg-green-50 border border-green-200'
            : 'bg-yellow-50 border border-yellow-200'
        }`}
      >
        {enabled && canEnable ? (
          <>
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="font-medium text-green-800">Email sending is enabled</p>
              <p className="text-sm text-green-700">
                Proposals and invoices can be sent via email
              </p>
            </div>
          </>
        ) : (
          <>
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">Email sending is disabled</p>
              <p className="text-sm text-yellow-700">
                {!canEnable
                  ? 'Configure a Reply-To address to enable email sending'
                  : 'Enable email sending to send proposals and invoices'}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <label
            htmlFor="email-enabled"
            className="text-sm font-medium text-gray-900"
          >
            Enable email sending
          </label>
          <p className="text-sm text-gray-600">
            Allow sending proposals and invoices directly via email
          </p>
        </div>
        <button
          id="email-enabled"
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => handleEnabledChange(!enabled)}
          disabled={!canEnable && !enabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
            enabled ? 'bg-purple-600' : 'bg-gray-300'
          } ${!canEnable && !enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {showEnableWarning && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Please enter a Reply-To email address before enabling email sending.
        </div>
      )}

      {/* Reply-To Email */}
      <div>
        <label
          htmlFor="reply-to"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Reply-To Email <span className="text-red-500">*</span>
        </label>
        <input
          id="reply-to"
          type="email"
          value={replyTo}
          onChange={(e) => handleReplyToChange(e.target.value)}
          placeholder="proposals@yourcompany.com"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">
          When clients reply to sent emails, their replies will go to this address
        </p>
      </div>

      {/* BCC List */}
      <div>
        <label
          htmlFor="bcc-list"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          BCC Recipients
        </label>
        <input
          id="bcc-list"
          type="text"
          value={bccList}
          onChange={(e) => handleBccListChange(e.target.value)}
          placeholder="accounting@company.com, admin@company.com"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">
          Comma-separated list of email addresses. These people receive a copy of every
          sent proposal and invoice.
        </p>
      </div>

      {/* Email Signature Section */}
      <div className="pt-6 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5 text-slate-600" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">Email Signature</h3>
            <p className="text-sm text-gray-600">
              These details appear at the bottom of sent emails
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Signature Name */}
          <div>
            <label
              htmlFor="sig-name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Name
            </label>
            <input
              id="sig-name"
              type="text"
              value={signature.name || ''}
              onChange={(e) => handleSignatureChange('name', e.target.value)}
              placeholder="Jamie Banks"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Signature Title */}
          <div>
            <label
              htmlFor="sig-title"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Title
            </label>
            <input
              id="sig-title"
              type="text"
              value={signature.title || ''}
              onChange={(e) => handleSignatureChange('title', e.target.value)}
              placeholder="Principal Architect"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Signature Company */}
          <div>
            <label
              htmlFor="sig-company"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Company
            </label>
            <input
              id="sig-company"
              type="text"
              value={signature.company || ''}
              onChange={(e) => handleSignatureChange('company', e.target.value)}
              placeholder="Banks Architecture"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Signature Phone */}
          <div>
            <label
              htmlFor="sig-phone"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Phone
            </label>
            <input
              id="sig-phone"
              type="tel"
              value={signature.phone || ''}
              onChange={(e) => handleSignatureChange('phone', e.target.value)}
              placeholder="604-813-0043"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Signature Email */}
          <div className="md:col-span-2">
            <label
              htmlFor="sig-email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Contact Email
            </label>
            <input
              id="sig-email"
              type="email"
              value={signature.email || ''}
              onChange={(e) => handleSignatureChange('email', e.target.value)}
              placeholder="jamie@banksarchitect.ca"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              This appears in your email signature (different from Reply-To)
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-gray-100">
        <Button
          type="button"
          onClick={handleSave}
          disabled={updateEmailSettings.isPending || (enabled && !canEnable)}
          className="inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {updateEmailSettings.isPending
            ? 'Saving...'
            : hasChanges
            ? 'Save Settings'
            : 'Saved'}
        </Button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Email Notifications Section (R2)                                    */}
      {/* ------------------------------------------------------------------ */}
      <div className="pt-6 border-t border-gray-200" data-testid="email-notifications-section">
        <div className="flex items-center gap-3 pb-4 border-b">
          <Bell className="w-5 h-5 text-slate-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Email Notifications</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose which emails you receive and where they are sent
            </p>
          </div>
        </div>

        {notifLoading ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <>
            {/* 4 Notification Toggles */}
            <div className="mt-4 space-y-3">
              {EMAIL_NOTIFICATION_TOGGLES.map((toggle) => {
                const isOn = emailTypes[toggle.key] ?? toggle.defaultOn;
                return (
                  <div
                    key={toggle.key}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 pr-4">
                      <p className="text-sm font-medium text-gray-900">{toggle.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{toggle.description}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isOn}
                      data-testid={`notif-toggle-${toggle.key}`}
                      onClick={() => handleNotifToggle(toggle.key)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 cursor-pointer ${
                        isOn ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isOn ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Notification Email Address */}
            <div className="mt-4">
              <label
                htmlFor="notif-email-address"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Notification email address
              </label>
              <input
                id="notif-email-address"
                type="email"
                data-testid="notif-email-address"
                value={emailAddress}
                onChange={(e) => handleNotifEmailChange(e.target.value)}
                placeholder={user?.email ?? 'your@email.com'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                All notification emails will be sent here. Defaults to your account email.
              </p>
            </div>

            {/* Save Notifications Button */}
            <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
              <Button
                type="button"
                data-testid="save-notif-preferences"
                onClick={handleNotifSave}
                disabled={updateNotifPrefs.isPending || !notifHasChanges}
                className="inline-flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {updateNotifPrefs.isPending
                  ? 'Saving...'
                  : notifHasChanges
                  ? 'Save Preferences'
                  : 'Saved'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
